/**
 * Photo placeholder + image surface.
 *
 * When `src` is supplied, renders an <img> with the given fit. When
 * `src` is absent, falls back to one of 6 cheeky-warm gradient classes
 * (.photo-gradient-1..6 defined in styles/base.css) with an optional
 * letter overlay — the design's signature placeholder treatment.
 *
 * `variant` 1..6 picks the gradient; pass deterministically (eg. from
 * a slug hash) so the same person renders the same colour every time.
 */
import { CSSProperties, ReactNode } from 'react';

type Props = {
  src?: string | null;
  variant?: 1 | 2 | 3 | 4 | 5 | 6;
  initial?: string;
  label?: string;
  rounded?: number;
  alt?: string;
  fit?: 'cover' | 'contain';
  style?: CSSProperties;
  children?: ReactNode;
};

export function Photo({
  src,
  variant = 1,
  initial,
  label,
  rounded = 20,
  alt = '',
  fit = 'cover',
  style,
  children,
}: Props) {
  const base: CSSProperties = {
    borderRadius: rounded,
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    ...style,
  };

  if (src) {
    return (
      <div style={base}>
        <img
          src={src}
          alt={alt}
          style={{ width: '100%', height: '100%', objectFit: fit, display: 'block' }}
          draggable={false}
        />
        {children}
      </div>
    );
  }

  return (
    <div className={`photo-gradient-${variant}`} style={base}>
      {initial && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-display)',
            fontSize: '38%',
            fontWeight: 300,
            color: 'rgba(255,255,255,0.5)',
            letterSpacing: -4,
            fontStyle: 'var(--display-italic)',
          }}
        >
          {initial}
        </div>
      )}
      {label && (
        <div
          style={{
            position: 'absolute',
            left: 10,
            bottom: 10,
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'rgba(255,255,255,0.75)',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </div>
      )}
      {children}
    </div>
  );
}

/** Deterministic 1..6 variant from any string (slug, id, name). */
export function photoVariant(seed: string): 1 | 2 | 3 | 4 | 5 | 6 {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return (((Math.abs(h) % 6) + 1) as 1 | 2 | 3 | 4 | 5 | 6);
}
